import { useState, useRef, useEffect } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { nanoid } from 'nanoid'
import axios from '/components/configs/axios'
import styles from './styles.module.scss'
import Button from '../../../elements/Button'
import ProgressIndicator from '../../../elements/ProgressIndicator'
import ErrorDisplay from './ErrorDisplay'
import { useRouter } from 'next/router'
import singlePropertyTranslations from '../singlePropertyTranslation'
import { flare } from '@flareapp/js'

// Get default translations for fallbacks
const defaultTranslations = singlePropertyTranslations()

// Timeout constants
const SUCCESS_MESSAGE_DURATION = 2000 // 2 seconds
const API_TIMEOUT = 60000 // 60 seconds

// Helper function to determine error type from error object
const determineErrorType = (error, currentStep, translations, locale) => {
  // Stripe card errors (declined, insufficient funds, etc.)
  if (error?.type === 'card_error' || error?.type === 'validation_error') {
    return {
      type: 'payment_failed',
      title:
        translations?.paymentFailed?.[locale] ||
        defaultTranslations.welcome.card.stripe.paymentFailed[locale] ||
        defaultTranslations.welcome.card.stripe.paymentFailed.en,
      message: error.message,
      canRetry: true
    }
  }

  // Payment-related errors (payment intent, confirmation)
  if (error?.type === 'payment_failed' || currentStep === 'payment') {
    return {
      type: 'payment_failed',
      title:
        translations?.paymentFailed?.[locale] ||
        defaultTranslations.welcome.card.stripe.paymentFailed[locale] ||
        defaultTranslations.welcome.card.stripe.paymentFailed.en,
      message:
        error.message ||
        translations?.paymentFailedMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.paymentFailedMessage[locale] ||
        defaultTranslations.welcome.card.stripe.paymentFailedMessage.en,
      canRetry: true
    }
  }

  // Payment status unclear (confirmation succeeded but paymentIntent missing)
  if (error?.type === 'payment_unclear') {
    return {
      type: 'payment_unclear',
      title:
        translations?.paymentUnclear?.[locale] ||
        defaultTranslations.welcome.card.stripe.paymentUnclear[locale] ||
        defaultTranslations.welcome.card.stripe.paymentUnclear.en,
      message:
        translations?.paymentUnclearMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.paymentUnclearMessage[locale] ||
        defaultTranslations.welcome.card.stripe.paymentUnclearMessage.en,
      canRetry: false
    }
  }

  // Reservation creation failures (after successful payment)
  if (currentStep === 'reservation') {
    return {
      type: 'reservation_failed',
      title:
        translations?.reservationFailed?.[locale] ||
        defaultTranslations.welcome.card.stripe.reservationFailed?.[locale] ||
        defaultTranslations.welcome.card.stripe.reservationFailed?.en ||
        translations?.reservationPending?.[locale] ||
        defaultTranslations.welcome.card.stripe.reservationPending[locale] ||
        defaultTranslations.welcome.card.stripe.reservationPending.en,
      message:
        translations?.reservationFailedMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.reservationFailedMessage?.[
          locale
        ] ||
        defaultTranslations.welcome.card.stripe.reservationFailedMessage?.en ||
        translations?.reservationPendingMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.reservationPendingMessage[
          locale
        ] ||
        defaultTranslations.welcome.card.stripe.reservationPendingMessage.en,
      canRetry: true,
      paymentSucceeded: true
    }
  }

  // Booking conflict (409 Conflict - dates no longer available)
  if (error?.response?.status === 409) {
    return {
      type: 'dates_unavailable',
      title:
        translations?.datesUnavailable?.[locale] ||
        defaultTranslations.welcome.card.stripe.datesUnavailable[locale] ||
        defaultTranslations.welcome.card.stripe.datesUnavailable.en,
      message:
        translations?.datesUnavailableMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.datesUnavailableMessage[
          locale
        ] ||
        defaultTranslations.welcome.card.stripe.datesUnavailableMessage.en,
      canRetry: false
    }
  }

  // Network errors (timeout, connection issues)
  if (
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ETIMEDOUT' ||
    error?.code === 'ECONNREFUSED' ||
    error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('timeout')
  ) {
    return {
      type: 'network_timeout',
      title:
        translations?.connectionTimeout?.[locale] ||
        defaultTranslations.welcome.card.stripe.connectionTimeout?.[locale] ||
        defaultTranslations.welcome.card.stripe.connectionTimeout?.en ||
        translations?.networkError?.[locale] ||
        defaultTranslations.welcome.card.stripe.networkError[locale] ||
        defaultTranslations.welcome.card.stripe.networkError.en,
      message:
        translations?.connectionTimeoutMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.connectionTimeoutMessage?.[
          locale
        ] ||
        defaultTranslations.welcome.card.stripe.connectionTimeoutMessage?.en ||
        translations?.networkErrorMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.networkErrorMessage[locale] ||
        defaultTranslations.welcome.card.stripe.networkErrorMessage.en,
      canRetry: true
    }
  }

  // Server timeouts (504 Gateway Timeout, 503 Service Unavailable)
  if (error?.response?.status === 504 || error?.response?.status === 503) {
    return {
      type: 'server_timeout',
      title:
        translations?.serverTimeout?.[locale] ||
        defaultTranslations.welcome.card.stripe.serverTimeout?.[locale] ||
        defaultTranslations.welcome.card.stripe.serverTimeout?.en ||
        'Server Timeout',
      message:
        translations?.serverTimeoutMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.serverTimeoutMessage?.[
          locale
        ] ||
        defaultTranslations.welcome.card.stripe.serverTimeoutMessage?.en ||
        'Our servers are taking longer than expected to respond. Please wait a few minutes and contact support if the issue persists.',
      canRetry: false
    }
  }

  // Rate limiting (429 Too Many Requests)
  if (error?.response?.status === 429) {
    return {
      type: 'rate_limit',
      title:
        translations?.rateLimitTitle?.[locale] ||
        defaultTranslations.welcome.card.stripe.rateLimitTitle?.[locale] ||
        defaultTranslations.welcome.card.stripe.rateLimitTitle?.en ||
        'Please Wait',
      message:
        translations?.rateLimitMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.rateLimitMessage?.[locale] ||
        defaultTranslations.welcome.card.stripe.rateLimitMessage?.en ||
        'Please wait a moment and try again. We received too many requests in a short time.',
      canRetry: true
    }
  }

  // Validation errors (422 Unprocessable Entity - Laravel validation)
  if (error?.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || {}
    const fieldNames = Object.keys(validationErrors)

    // Helper to sanitize text for safe rendering - strips HTML tags and trims
    // Uses DOMParser when available (client-side) for ReDoS-safe parsing
    const sanitizeText = (str) => {
      if (typeof str !== 'string') return ''
      if (typeof window !== 'undefined' && window.DOMParser) {
        const doc = new DOMParser().parseFromString(str, 'text/html')
        return (doc.body.textContent || '').trim()
      }
      // Server-side fallback with simple character stripping (no regex)
      let result = ''
      let inTag = false
      for (let i = 0; i < str.length; i++) {
        if (str[i] === '<') inTag = true
        else if (str[i] === '>') inTag = false
        else if (!inTag) result += str[i]
      }
      return result.trim()
    }

    // Helper to safely get translation string, sanitize it, and remove trailing colon
    const getFieldLabel = (translationValue, fallback) => {
      if (typeof translationValue === 'string') {
        return sanitizeText(translationValue.replace(':', ''))
      }
      return fallback
    }

    // Translation path shortcuts - welcome.card is guaranteed in translation structure
    const formFields = translations?.welcome?.card?.form?.fields
    const defaultFormFields = defaultTranslations.welcome.card.form.fields
    const popup = translations?.welcome?.card?.popup
    const defaultPopup = defaultTranslations.welcome.card.popup
    const card = translations?.welcome?.card
    const defaultCard = defaultTranslations.welcome.card

    // Strict whitelist of allowed field names from Laravel validation
    // Only these fields are displayed - unknown fields are silently ignored
    // This prevents XSS by never rendering arbitrary backend response data
    const fieldLabels = {
      firstName: getFieldLabel(
        formFields?.firstName?.[locale] ||
          defaultFormFields.firstName?.[locale],
        'First Name'
      ),
      lastName: getFieldLabel(
        formFields?.lastName?.[locale] || defaultFormFields.lastName?.[locale],
        'Last Name'
      ),
      email: getFieldLabel(
        formFields?.email?.[locale] || defaultFormFields.email?.[locale],
        'Email'
      ),
      startDate: getFieldLabel(
        popup?.arrival?.[locale] || defaultPopup.arrival?.[locale],
        'Arrival Date'
      ),
      endDate: getFieldLabel(
        popup?.departure?.[locale] || defaultPopup.departure?.[locale],
        'Departure Date'
      ),
      amount: getFieldLabel(
        card?.total?.[locale] || defaultCard.total?.[locale],
        'Amount'
      ),
      guests: getFieldLabel(
        card?.guests?.[locale] || defaultCard.guests?.[locale],
        'Guests'
      ),
      bedrooms: getFieldLabel(
        card?.bedrooms?.[locale] || defaultCard.bedrooms?.[locale],
        'Bedrooms'
      )
    }

    // Build user-friendly field list using only whitelisted fields
    // Uses hasOwnProperty to prevent prototype pollution attacks
    // Unknown fields from backend are ignored for security
    const friendlyFieldNames = fieldNames
      .filter((field) =>
        Object.prototype.hasOwnProperty.call(fieldLabels, field)
      )
      .map((field) => fieldLabels[field])
      .filter((label) => typeof label === 'string' && label.length > 0)
      .join(', ')

    const formError = translations?.welcome?.card?.form?.error
    const defaultFormError = defaultTranslations.welcome.card.form.error
    const stripe = translations?.welcome?.card?.stripe
    const defaultStripe = defaultTranslations.welcome.card.stripe

    const baseMessage =
      formError?.requiredFields?.[locale] ||
      defaultFormError.requiredFields?.[locale] ||
      defaultFormError.requiredFields?.en ||
      'Please fill in the following required fields'

    // Handle empty field names case (all fields were unknown/not whitelisted)
    // Note: We intentionally do NOT fall back to error.response?.data?.message
    // as it may contain sensitive backend details or stack traces
    const message =
      friendlyFieldNames.length > 0
        ? `${sanitizeText(baseMessage)}: ${friendlyFieldNames}`
        : sanitizeText(baseMessage) || 'Please check your input and try again'

    return {
      type: 'validation_error',
      title:
        stripe?.validationError?.[locale] ||
        defaultStripe.validationError?.[locale] ||
        defaultStripe.validationError?.en ||
        'Required Fields Missing',
      message,
      canRetry: true
    }
  }

  // Default unknown error
  return {
    type: 'unknown_error',
    title:
      translations?.unknownError?.[locale] ||
      defaultTranslations.welcome.card.stripe.unknownError?.[locale] ||
      defaultTranslations.welcome.card.stripe.unknownError?.en ||
      'Something Went Wrong',
    message:
      error.message ||
      translations?.unknownErrorMessage?.[locale] ||
      defaultTranslations.welcome.card.stripe.unknownErrorMessage[locale] ||
      defaultTranslations.welcome.card.stripe.unknownErrorMessage.en,
    canRetry: false
  }
}

const CheckoutForm = ({
  total,
  propertyId,
  listingId,
  startDate,
  endDate,
  bedrooms,
  email,
  firstName,
  lastName,
  setPlans,
  guests,
  setReservationId,
  propertyName,
  coupon,
  setSkip,
  bonuses,
  freeNights,
  freeNightlyRate,
  initialPrice,
  translations,
  manually_blocked = false,
  status = 'pending',
  setReservationData
}) => {
  const [message, setMessage] = useState(null)
  const [paymentDone, setPaymentDone] = useState(false)

  // Enhanced submission state for progress tracking and duplicate prevention
  const [submissionState, setSubmissionState] = useState({
    isSubmitting: false,
    currentStep: null,
    canRetry: false,
    error: null
  })

  // Refs to store timeout IDs for cleanup
  const successTimeoutRef = useRef(null)
  const errorTimeoutRef = useRef(null)
  // Ref to prevent race conditions in duplicate submission check
  const isSubmittingRef = useRef(false)
  // Ref to track component mount state to prevent state updates on unmounted component
  const isMountedRef = useRef(true)
  // Ref for error display to manage focus for accessibility
  const errorRef = useRef(null)

  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()

  // Cleanup timeouts on component unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
      }
    }
  }, [])

  // Focus error display when error occurs for accessibility
  useEffect(() => {
    if (submissionState.error && errorRef.current) {
      errorRef.current.focus()
    }
  }, [submissionState.error])

  // Prevent accidental navigation during payment processing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isSubmittingRef.current) {
        const message =
          translations?.processingPaymentWarning?.[router.locale] ||
          defaultTranslations.welcome.card.stripe.processingPaymentWarning[
            router.locale
          ] ||
          defaultTranslations.welcome.card.stripe.processingPaymentWarning.en

        e.preventDefault()
        e.returnValue = message // Standard for most browsers
        return message // For some older browsers
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [router.locale, translations])

  if (!stripe || !elements) {
    return (
      <div className={styles.payment_form}>
        <ProgressIndicator
          currentStep="loading"
          message={
            translations?.loadingPayment?.[router.locale] ||
            'Loading payment form...'
          }
        />
      </div>
    )
  }

  // Helper function to get step message based on current step
  const getStepMessage = (step) => {
    const messages = {
      calculating:
        translations?.calculating?.[router.locale] || 'Calculating...',
      payment:
        translations?.processingPayment?.[router.locale] ||
        'Processing payment...',
      reservation:
        translations?.creatingReservation?.[router.locale] ||
        'Creating reservation...',
      confirmation:
        translations?.sendingConfirmation?.[router.locale] ||
        'Sending confirmation...'
    }

    return (
      messages[step] ||
      translations?.confirming?.[router.locale] ||
      'Processing...'
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Clear any existing timeouts from previous submissions FIRST to prevent race conditions
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current)
      successTimeoutRef.current = null
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current)
      errorTimeoutRef.current = null
    }

    // Prevent duplicate submissions using ref to avoid race conditions
    if (isSubmittingRef.current) {
      return
    }
    isSubmittingRef.current = true

    // Generate idempotency key for this submission to prevent duplicate charges
    // Use nanoid() for cryptographically strong uniqueness (secure, tiny, URL-safe)
    const randomId = nanoid(8) // 8 characters, cryptographically secure
    const idempotencyKey = `booking-${propertyId}-${Date.now()}-${randomId}`

    // Initialize submission state
    setSubmissionState({
      isSubmitting: true,
      currentStep: 'calculating',
      canRetry: false,
      error: null
    })

    // Declare paymentIntentId outside try block so it's accessible in catch
    let paymentIntentId = null

    try {
      // Step 1: Price calculation
      setSubmissionState((prev) => ({ ...prev, currentStep: 'calculating' }))
      await axios.post(
        'reservation/price-calculation',
        {
          lang: router.locale,
          id: propertyId,
          total: Math.round((total || 0) * 100),
          startDate: startDate,
          endDate: endDate,
          bedrooms: bedrooms
        },
        {
          timeout: API_TIMEOUT,
          headers: { 'Idempotency-Key': idempotencyKey }
        }
      )

      // Step 2: Create payment method and process payment
      setSubmissionState((prev) => ({ ...prev, currentStep: 'payment' }))
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement)
      })

      if (error) {
        const paymentError = new Error(error.message)
        paymentError.type = 'payment_failed'
        paymentError.original = error
        throw paymentError
      }

      const { id } = paymentMethod

      // Step 3: Create payment intent
      const intentResponse = await axios.post(
        'reservation/payment-intent',
        {
          amount: Math.round((total || 0) * 100),
          firstName: firstName,
          lastName: lastName,
          email: email
        },
        {
          timeout: API_TIMEOUT,
          headers: { 'Idempotency-Key': `${idempotencyKey}-intent` }
        }
      )

      // Defensive check for successful HTTP status codes (2xx range)
      // While Axios throws for non-2xx by default, this explicit check ensures
      // we handle edge cases where custom interceptors or config might modify behavior
      if (intentResponse.status < 200 || intentResponse.status >= 300) {
        const intentError = new Error(
          intentResponse.data?.message || 'Failed to create payment intent'
        )
        intentError.type = 'payment_failed'
        throw intentError
      }

      const clientKey = intentResponse.data[0]
      paymentIntentId = intentResponse.data[1]
      const customerId = intentResponse.data[2]

      // Validate that all required payment intent data is present
      if (!clientKey || !paymentIntentId || !customerId) {
        const validationError = new Error(
          'Invalid payment intent response: missing required fields'
        )
        validationError.type = 'payment_failed'
        validationError.details = {
          hasClientKey: !!clientKey,
          hasPaymentIntentId: !!paymentIntentId,
          hasCustomerId: !!customerId
        }
        throw validationError
      }

      // Step 4: Confirm payment
      const confirmation = await stripe.confirmCardPayment(clientKey, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: firstName + ' ' + lastName
          }
        }
      })

      if (!confirmation.paymentIntent) {
        // Critical: Distinguish between actual payment failure vs unclear payment status
        if (confirmation.error) {
          // Payment actually failed - safe to retry
          const confirmationError = new Error(confirmation.error.message)
          confirmationError.type = 'payment_failed'
          confirmationError.original = confirmation.error
          throw confirmationError
        } else {
          // Payment may have succeeded but we lost connection or got unclear response
          // DO NOT allow retry to prevent double charges

          // Log to Flare with full transaction context for manual reconciliation
          flare.report(
            new Error(
              'Payment Status Unclear - Manual Reconciliation Required'
            ),
            {
              context: {
                paymentIntentId,
                customerId,
                propertyId,
                listingId,
                propertyName,
                amount: Math.round((total || 0) * 100),
                amountFormatted: total,
                startDate,
                endDate,
                bedrooms,
                guests,
                email,
                firstName,
                lastName,
                idempotencyKey,
                locale: router.locale
              },
              severity: 'critical',
              message: `Payment confirmation failed to return paymentIntent. Customer may have been charged. PaymentIntentId: ${paymentIntentId}. Support should check Stripe dashboard and contact customer at ${email}.`
            }
          )

          // ESLint: process.env is injected by Next.js at build time
          /* eslint-disable no-undef */
          const supportEmail =
            process.env.NEXT_PUBLIC_SUPPORT_EMAIL || 'reservations@sxmsir.com'
          /* eslint-enable no-undef */
          const unclearError = new Error(
            `Payment status unclear. Reference ID: ${paymentIntentId}. Please contact support at ${supportEmail} with this reference ID.`
          )
          unclearError.type = 'payment_unclear'
          unclearError.paymentIntentId = paymentIntentId
          throw unclearError
        }
      }

      // Step 5: Create reservation
      setSubmissionState((prev) => ({ ...prev, currentStep: 'reservation' }))
      const reservationResponse = await axios.post(
        'reservation/guesty',
        {
          firstName: firstName,
          lastName: lastName,
          email: email,
          startDate: startDate,
          endDate: endDate,
          propertyId: propertyId,
          listingId: listingId,
          amount: Math.round((total || 0) * 100),
          paymentIntentId: paymentIntentId,
          customerId: customerId,
          bedrooms: bedrooms,
          coupon,
          id,
          guests,
          bonuses,
          freeNights,
          freeNightlyRate: Math.round((freeNightlyRate || 0) * 100),
          initialPrice: Math.round((initialPrice || 0) * 100),
          manually_blocked
        },
        {
          timeout: API_TIMEOUT,
          headers: { 'Idempotency-Key': `${idempotencyKey}-reservation` }
        }
      )

      // Step 6: Success - backend sends confirmation email
      setSubmissionState((prev) => ({ ...prev, currentStep: 'confirmation' }))

      // Success! Reset state and show success message
      if (isMountedRef.current) {
        setSubmissionState({
          isSubmitting: false,
          currentStep: null,
          canRetry: false,
          error: null
        })
        setMessage(translations?.paymentSucceeded[router.locale])
        setPaymentDone(true)

        // Store full reservation data for confirmation display
        setReservationId(reservationResponse.data.reservationId)

        // Store reservation details if setReservationData prop is provided
        if (typeof setReservationData === 'function') {
          setReservationData(reservationResponse.data.reservation)
        }

        successTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setMessage(null)
            setPlans('thankYou')
          }
        }, SUCCESS_MESSAGE_DURATION)
      }
    } catch (error) {
      // Capture payment intent ID if we got that far
      const capturedPaymentIntentId =
        paymentIntentId || error?.paymentIntentId || null

      // Handle errors with enhanced error type detection
      const errorInfo = determineErrorType(
        error,
        submissionState.currentStep,
        translations,
        router.locale
      )

      // Log failed payment attempt to backend (fire-and-forget)
      try {
        await axios.post(
          'reservation/log-failed-attempt',
          {
            propertyId,
            listingId,
            propertyName,
            firstName,
            lastName,
            email,
            startDate,
            endDate,
            bedrooms,
            guests,
            amount: Math.round((total || 0) * 100),
            amountFormatted: total,
            coupon,
            bonuses,
            freeNights,
            freeNightlyRate: Math.round((freeNightlyRate || 0) * 100),
            initialPrice: Math.round((initialPrice || 0) * 100),
            manually_blocked,
            paymentIntentId: capturedPaymentIntentId,
            idempotencyKey,
            failedStep: submissionState.currentStep,
            errorType: errorInfo.type,
            errorMessage: errorInfo.message,
            locale: router.locale
          },
          {
            timeout: 5000 // Short timeout - don't delay user feedback
          }
        )
      } catch (logError) {
        // Silently fail - logging shouldn't affect user experience
        console.error('Failed to log payment attempt:', logError)
      }

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setSubmissionState({
          isSubmitting: false,
          currentStep: null,
          canRetry: errorInfo.canRetry,
          error: { ...errorInfo, paymentIntentId: capturedPaymentIntentId }
        })
      }

      // Do not auto-clear non-retryable errors - user needs to see critical information
      // Critical errors (like payment charged but reservation failed) should not disappear
    } finally {
      // Reset submission ref to allow retry if applicable
      isSubmittingRef.current = false
    }
  }

  return (
    <div>
      <form className={styles.payment_form} onSubmit={handleSubmit}>
        <CardElement />

        <Button
          variant={['color-accent', 'full-width']}
          arrow={!submissionState.isSubmitting}
          disabled={
            submissionState.isSubmitting || !stripe || !elements || paymentDone
          }
        >
          <span id="button-text">
            {submissionState.isSubmitting ? (
              <div className={styles.label}>
                <ProgressIndicator
                  currentStep={submissionState.currentStep}
                  message={getStepMessage(submissionState.currentStep)}
                />
              </div>
            ) : submissionState.canRetry ? (
              translations?.tryAgain?.[router.locale] ||
              translations?.confirm?.[router.locale]
            ) : (
              translations?.confirm?.[router.locale]
            )}
          </span>
        </Button>
        {message && <div className={styles.payment_message}>{message}</div>}

        {submissionState.error && (
          <ErrorDisplay
            ref={errorRef}
            error={submissionState.error}
            translations={translations}
            locale={router.locale}
            paymentIntentId={submissionState.error?.paymentIntentId}
          />
        )}

        <div
          dangerouslySetInnerHTML={{
            __html: translations?.note[router.locale]
          }}
          className={styles.note}
        ></div>
      </form>

      <div className={styles.skip}>
        <Button
          variant={['label', 'xs']}
          onClick={async () => {
            try {
              // DEPLOYMENT NOTE: Skip payment email notification
              // This email call MUST remain active - it handles a different workflow
              // where the customer requests to complete payment outside the system.
              // This is not a completed reservation, so backend doesn't handle it.
              await axios.post('email/skip-payment', {
                propertyId: propertyId,
                listingId: listingId,
                total: Math.round((total || 0) * 100),
                propertyName,
                startDate,
                endDate,
                bedrooms,
                email,
                firstName,
                lastName,
                guests,
                coupon,
                bonuses,
                freeNights,
                freeNightlyRate: Math.round((freeNightlyRate || 0) * 100),
                initialPrice: Math.round((initialPrice || 0) * 100),
                manually_blocked,
                status
              })
              setMessage(null)
              setPlans('finish')
              setSkip(true)
            } catch (error) {
              console.error('Skip payment error:', error)
              // Optionally show error to user
            }
          }}
        >
          {translations?.skip[router.locale]}
        </Button>

        <p>{translations?.skipNote[router.locale]}</p>
      </div>
    </div>
  )
}

export default CheckoutForm
